<template>
    <b-container fluid>
        <b-row class="mb-4 mb-md-4 mb-xl-5">
            <b-col cols lg="12" xl="4" class="mb-4 mb-md-4 mb-xl-0">
                <b-row class="h-100">
                    <b-col cols="12" md="6" xl="12" class="mb-4 mb-md-0 mb-xl-0">
                        <qesg-dash-card
                            :title="$t('총 참여기업')"
                            :index="`${stats.total} ${$i18n.locale == 'ko' ? '개 기업': ''}`"
                            icon="building"
                            icon-variant="info"
                        />
                    </b-col>
                    <b-col cols="12" md="6" xl="12" class="align-self-md-end">
                        <qesg-dash-card
                            :title="$t('총 평가완료 기업')"
                            :index="`${stats.fields['완료됨']} ${$i18n.locale == 'ko' ? '개 기업': ''}`"
                            icon="clipboard-check"
                            iconVariant="primary"
                        />
                    </b-col>
                </b-row>
            </b-col>
            <b-col cols lg="12" xl="8" class="">
                <qesg-dash-chart-card
                    icon="clipboard-data"
                    icon-variant="info"
                >
                    <template v-slot:header-title>
                        <h5 class="dash-card-title mb-0">
                            {{ $t('총 참가기업 현황') }}
                            <b-icon-info-circle v-b-popover.hover="statsPopoverConfig" variant="secondary" scale="0.7"
                                                class="ml-0 mb-1"/>
                        </h5>
                    </template>

                    <b-row class="justify-content-xl-around py-2">
                        <b-col cols md="5" lg="5" xl="5">
                            <loading-view
                                :is-loading="isLoadProjectData"
                                size="sm"
                                variant="dark"
                            >
                                <h6 class="font-italic font-weight-normal"> {{ $t('대기중') }}... </h6>
                            </loading-view>
                            <div class="chart-container mb-3 mb-md-0"
                                 style="height: 235px;"
                            >
                                <canvas id="progress-pie-chart"/>
                            </div>
                        </b-col>
                        <b-col cols md="7" lg="6" xl="7" sl="5">
                            <b-row id="chart-progress-legend"
                                   class="align-items-center justify-content-center justify-content-xl-end h-100 pr-lg-5 pr-xl-5">
                                <b-col v-for="(value, key, index) in stats.fields"
                                       :key="`progress-stats-${index}`"
                                       cols="6" md="6"
                                       :class="[index > 1 ? 'mt-4 mt-md-3' : 'mt-4 mt-md-0', 'user-select-none']"
                                >
                                    <!-- {{ index }} -->
                                    <div class="d-flex align-items-center mb-1">
                                        <div class="rounded mr-2"
                                             :style="{ width: '17px', height: '17px', backgroundColor: stats.colors[key] }"
                                        />
                                        <div class="">
                                            {{ $t(key) }}:
                                        </div>
                                        <div class="flex-grow-1 text-right">
                                            {{ value }} {{ $i18n.locale == 'ko' ? '개' : '' }}
                                        </div>
                                    </div>
                                    <div class="">
                                        <b-btn
                                            variant="light"
                                            size="sm"
                                            :title="`${key} ${ $t('상태의 기업 보기') }`"
                                            :disabled="isParticipantsTableBusy"
                                            block
                                            @click="viewParticipantByLegend(key)"
                                        >
                                            <BIconSearch font-scale="0.9" shift-v="3" class="ml-1"/>
                                        </b-btn>
                                    </div>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </qesg-dash-chart-card>
            </b-col>
        </b-row>
        <b-row class="mb-4 mb-md-4 mb-xl-5">
            <b-col cols="12">
                <qesg-dash-card-wrapper no-body>
                    <b-row align-v="center" no-gutters class="border-bottom border-color-gray-300 px-3 mb-3 pt-2 pb-3">
                        <b-col cols>
                            <h5 class="dash-card-title mb-0">{{ $t('프로젝트 기간') }}</h5>
                        </b-col>
                        <b-col cols="auto">
                            <div class="d-flex">
                                <qesg-dash-card-icon icon="calendar3" variant="secondary"/>
                            </div>
                        </b-col>
                    </b-row>

                    <b-list-group horizontal="md">
                        <b-list-group-item class="d-flex align-items-center flex-grow-1 border-0">
                            <div class="d-block d-md-none d-lg-block mr-2">
                                <qesg-dash-card-icon icon="calendar-check" variant="info" :font-scale="2" class="mr-2"/>
                            </div>

                            <div class="d-inline-block">
                                <div class="d-none d-md-inline-flex d-lg-none">
                                    <qesg-dash-card-icon icon="calendar-check" variant="info" :font-scale="0.9"
                                                         class="d-none d-md-flex d-lg-none mr-2"/>
                                </div>

                                <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">
                                    {{ $t('목표 기간') }}</h6>
                                <h4 v-if="$i18n.locale === 'ko'"
                                    class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.target_end_date_dis) }}</h4>
                                <h4 v-else class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.target_end_date_dis_en) }}</h4>
                            </div>
                        </b-list-group-item>
                        <b-list-group-item class="d-flex align-items-center flex-grow-1 border-0">
                            <div class="d-block d-md-none d-lg-block mr-2">
                                <qesg-dash-card-icon icon="calendar4-Event" variant="info" :font-scale="2"
                                                     class="mr-2"/>
                            </div>

                            <div class="d-inline-block">
                                <div class="d-none d-md-inline-flex d-lg-none">
                                    <qesg-dash-card-icon icon="calendar4-Event" variant="info" :font-scale="0.9"
                                                         class="d-none d-md-flex d-lg-none mr-2"/>
                                </div>

                                <!-- <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">{{ $t('프로젝트 코드') }} {{ $t('시작일') }}</h6> -->
                                <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">
                                    {{ $t('프로젝트') }} {{ $t('시작일') }}
                                </h6>
                                <h4 v-if="$i18n.locale === 'ko'"
                                    class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.start_date_dis) }}</h4>
                                <h4 v-else class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.start_date_dis_en) }}</h4>
                            </div>

                        </b-list-group-item>
                        <b-list-group-item class="d-flex align-items-center flex-grow-1 border-0">
                            <div class="d-block d-md-none d-lg-block mr-2">
                                <qesg-dash-card-icon icon="calendar4-week" variant="info" :font-scale="2" class="mr-2"/>
                            </div>

                            <div class="d-inline-block">
                                <div class="d-none d-md-inline-flex d-lg-none">
                                    <qesg-dash-card-icon icon="calendar4-week" variant="info" :font-scale="0.9"
                                                         class="d-none d-md-flex d-lg-none mr-2"/>
                                </div>

                                <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">
                                    {{ $t('프로젝트') }} {{ $t('종료일') }}
                                </h6>
                                <h4 v-if="$i18n.locale === 'ko'"
                                    class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.end_date_dis) }}</h4>
                                <h4 v-else class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.end_date_dis_en) }}</h4>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </qesg-dash-card-wrapper>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="12">
                <qesg-dash-table-card class="h-100" icon="table">
                    <template v-slot:title>
                        <h5 class="d-inline-block font-weight-normal mb-0 mr-2">
                            <span class="text-secondary">{{ $t('기업별 진행현황') }}</span>
                            <b-badge id="popover-participant-count" variant="info" class="text-white ml-2" pill>
                                <small>{{ currentParticipantsCount.total }} {{
                                        $i18n.locale == 'ko' ? '개 기업' : ''
                                    }}</small>
                            </b-badge>
                        </h5>
                        <b-popover target="popover-participant-count" triggers="hover">
                            <template #title>기업별 진행현황 도움말</template>
                            좌측은 <strong>총 기업수</strong>로, <br/> 테이블 하단 중앙의 <strong>+ (더하기) 버튼</strong>을 클릭하시면 참여기업을 더 보실
                            수 있으십니다.
                        </b-popover>
                    </template>
                    <b-row no-gutters class="border-top border-bottom border-color-gray-300 py-3 px-3">
                        <b-col cols="12" md="6" lg="4">
                            <div class="d-flex align-items-center justify-content-end">
                                <b-input-group>
                                    <b-form-input v-model="participnatfilterForm.company_name"
                                                  :title="$t('참여기업 이름 검색')"
                                                  :placeholder="$t('참여기업 이름을 입력해주세요')"
                                                  trim
                                                  @keyup.enter="searchParticipantByName"
                                    />
                                    <b-input-group-append>
                                        <b-btn
                                            :title="$t('참여기업 이름 검색')"
                                            :disabled="isParticipantsTableBusy"
                                            @click="searchParticipantByName"
                                        >
                                            <BIconSearch/>
                                        </b-btn>
                                        <b-btn
                                            variant="light"
                                            class="border"
                                            :disabled="isParticipantsTableBusy"
                                            :title="$t('조건 검색')"
                                            @click="collapsedParticipantFilterForm = !collapsedParticipantFilterForm"
                                        >
                                            <B-Icon-filter variant=""/>
                                        </b-btn>
                                    </b-input-group-append>
                                </b-input-group>
                            </div>
                        </b-col>
                        <b-col cols="6" md="6" offset-md="2" class="d-none d-md-block text-right">
                            <b-btn-group>
                                <b-btn
                                    variant="light"
                                    class="border ml-auto"
                                    :disabled="isParticipantsTableBusy"
                                    :title="$t('참여기업 엑셀 다운로드')"
                                    @click="downloadParticipantsExcel"
                                >
                                    <b-icon-download/>
                                </b-btn>
                                <b-btn
                                    variant="light"
                                    class="border mr-2"
                                    :disabled="isParticipantsTableBusy"
                                    :title="$t('검색 초기화')"
                                    @click="refreshParticipants"
                                >
                                    <b-icon-arrow-repeat/>
                                </b-btn>
                            </b-btn-group>
                            <b-form-select
                                v-model="selectedParticipantTablePerPage"
                                :options="participantTablePerPageOpt"
                                :disabled="isParticipantsTableBusy"
                                class="w-auto"
                            />
                        </b-col>
                    </b-row>
                    <b-collapse id="collapse-participant-table-filter" v-model="collapsedParticipantFilterForm">
                        <b-row>
                            <b-col cols>
                                <div class="border bg-light rounded p-3 m-3">
                                    <b-form
                                        @submit.prevent="onSubmitParticipantFilter"
                                        @reset="onResetParticipantFilter(true)"
                                    >
                                        <b-form-row>
                                            <b-col cols="12" md="4">
                                                <b-form-group
                                                    id="participant-filter-name"
                                                    :label="$t('참여기업명')"
                                                    label-for="input-participant-name"
                                                >
                                                    <b-form-input
                                                        id="input-participant-name"
                                                        v-model="participnatfilterForm.company_name"
                                                        type="text"
                                                        :placeholder="$t('참여기업 이름을 입력해주세요')"
                                                        :disabled="isParticipantsTableBusy"
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="4">
                                                <b-form-group
                                                    id="participant-filter-campaign"
                                                    :label="$t('캠페인')"
                                                    label-for="input-participant-campaign"
                                                >
                                                    <b-form-select
                                                        v-model="participnatfilterForm.campaign_id"
                                                        :disabled="isParticipantsTableBusy"
                                                        class="w-100"
                                                    >
                                                        <b-form-select-option :value="null">{{
                                                                $t('캠페인을 선택해주세요.')
                                                            }}
                                                        </b-form-select-option>
                                                        <b-form-select-option
                                                            v-for="(campaign, index) in selectableCampaigns"
                                                            :key="`participant-filter-campaign-${index}`"
                                                            :value="campaign.id"
                                                        >
                                                            {{ campaign.name }}
                                                        </b-form-select-option>
                                                    </b-form-select>
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="4">
                                                <b-form-group
                                                    id="participant-filter-status_code"
                                                    v-slot="{ ariaDescribedby }"
                                                    :label="$t('평가참여 상태')"
                                                    label-for="checkbox-participant-status_code"
                                                >
                                                    <b-form-checkbox-group
                                                        id="checkbox-participant-status_code-group"
                                                        v-model="participnatfilterForm.status_code"
                                                        :aria-describedby="ariaDescribedby"
                                                        name="checkbox-participant-status_code-group"
                                                    >
                                                        <b-form-checkbox v-for="(val, key, s) in statsLabel"
                                                                         :key="`participant-status_code-checkbox-${s}-${val}`"
                                                                         :value="val"
                                                                         :disabled="isParticipantsTableBusy"
                                                                         inline
                                                        >
                                                            {{ $t(key) }}
                                                        </b-form-checkbox>
                                                    </b-form-checkbox-group>
                                                </b-form-group>
                                            </b-col>
                                        </b-form-row>
                                        <b-form-row>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('신청일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-requested_ts_start"
                                                    label-for="datepicker-participant-requested_ts_start"
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-requested_ts_start"
                                                        v-model="participnatfilterForm.requested_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-requested_ts_end"
                                                    label-for="datepicker-participant-requested_ts_end"
                                                >
                                                    <!-- label="신청 - 종료일" -->
                                                    <b-form-datepicker
                                                        id="datepicker-participant-requested_ts_end"
                                                        v-model="participnatfilterForm.requested_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('평가일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-in_assessment_ts_start"
                                                    label-for="datepicker-participant-in_assessment_ts_start"
                                                >
                                                    <!-- label="평가 - 시작일" -->
                                                    <b-form-datepicker
                                                        id="datepicker-participant-in_assessment_ts_start"
                                                        v-model="participnatfilterForm.in_assessment_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-in_assessment_ts_end"
                                                    label-for="datepicker-participant-in_assessment_ts_end"
                                                >
                                                    <!-- label="평가 - 종료일" -->
                                                    <b-form-datepicker
                                                        id="datepicker-participant-in_assessment_ts_end"
                                                        v-model="participnatfilterForm.in_assessment_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('완료일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-completed_ts_start"
                                                    label-for="datepicker-participant-completed_ts_start"
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-completed_ts_start"
                                                        v-model="participnatfilterForm.completed_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-completed_ts_end"
                                                    label-for="datepicker-participant-completed_ts_end"
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-completed_ts_end"
                                                        v-model="participnatfilterForm.completed_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('마감일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-completed_ts_end"
                                                    label-for="datepicker-participant-duedate_ts_start"
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-duedate_ts_start"
                                                        v-model="participnatfilterForm.duedate_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-completed_ts_end"
                                                    label-for="datepicker-participant-duedate_ts_end"
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-duedate_ts_end"
                                                        v-model="participnatfilterForm.duedate_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                        </b-form-row>
                                        <b-form-row>
                                            <b-col cols>
                                                <hr>
                                            </b-col>
                                        </b-form-row>
                                        <b-form-row>
                                            <b-col class="text-right">
                                                <b-btn type="submit" variant="primary" class="mr-2"
                                                       :disabled="isParticipantsTableBusy">{{ $t('조회') }}
                                                </b-btn>
                                                <b-btn type="reset" variant="danger" class="mr-2"
                                                       :disabled="isParticipantsTableBusy">{{ $t('초기화') }}
                                                </b-btn>
                                                <b-button
                                                    variant="secondary"
                                                    :disabled="isParticipantsTableBusy"
                                                    @click="collapsedParticipantFilterForm = !collapsedParticipantFilterForm"
                                                >
                                                    {{ $t('닫기') }}
                                                </b-button>
                                            </b-col>
                                        </b-form-row>
                                    </b-form>
                                </div>
                            </b-col>
                        </b-row>
                    </b-collapse>
                    <b-row no-gutters>
                        <b-col cols>
                            <b-table
                                id="total-participant-table"
                                class="border-bottom border-color-gray-300"
                                thead-class="sticky-header-sort-fix"
                                :items="participants"
                                :fields="participantsFields"
                                :busy="isParticipantsTableBusy"
                                :empty-html="`<h5 class='mt-3 gray-500'>${ $t('조회된 참여기업이 없습니다.') }</h5>`"
                                details-td-class="bg-light"
                                show-empty
                                responsive
                                hover
                                sticky-header="75vh"
                                no-border-collapse
                                :per-page="selectedParticipantTablePerPage"
                                :current-page="participantTableCurrentPage"
                            >
                                <template #table-busy>
                                    <div class="text-center text-primary my-3">
                                        <b-spinner class="align-middle mr-3"></b-spinner>
                                        <strong> {{ participantsTableStatusStatement }}</strong>
                                    </div>
                                </template>

                                <template #empty="scope">
                                    <div class="text-center" v-html="scope.emptyHtml"></div>
                                </template>

                                <template #head(index)="data">
                                    <div class="text-center">{{ data.label }}</div>
                                </template>

                                <template #head(show_details)="data">
                                    <div class="d-inline-block text-center mr-1">{{ data.label }}</div>
                                    <b-icon-info-circle id="popover-participant-table-details" variant="secondary"
                                                        scale="1" class="ml-1"/>
                                    <b-popover target="popover-participant-table-details" triggers="hover">
                                        <template #title>{{ $t('상세내용 도움말') }}</template>
                                        <div v-if="$i18n.locale === 'ko'">
                                            아래 <strong>상세내용 보기</strong> 버튼을 클릭하시면 참여기업의 신청일, 답변일 등의 날짜와 담당자 이메일 등 상세내용을
                                            보실 수 있습니다.
                                        </div>
                                        <div v-else-if="$i18n.locale === 'en'">
                                            Click the <strong>View Details</strong> button below to see detailed
                                            information such as application dates, response dates, and manager's email
                                            for the participating company.
                                        </div>
                                        <div v-else>
                                            点击下面的 <strong>查看详细信息</strong>
                                            按钮，您可以查看参与公司的申请日期、回答日期等详细信息，以及负责人的电子邮件。
                                        </div>
                                    </b-popover>
                                </template>

                                <template #cell(index)="data">
                                    <div class="text-center">
                                        {{
                                            participantTableCurrentPage === 1 ? data.index + 1 : ((participantTableCurrentPage - 1) * 100) + data.index + 1
                                        }}
                                    </div>
                                </template>

                                <template #cell(status_code)="data">
                                    <div class="d-inline px-2 py-1 text-white rounded-pill small"
                                         :style="[{backgroundColor: stats.colors[statsLabel_eng[data.value]]}]"
                                    >
                                        {{ $t(statsLabel_eng[data.value]) }}
                                    </div>
                                </template>


                                <template #cell(service_type)="data">
                                    {{ data.value ? $t(`${data.value} 평가`) : '-' }}
                                </template>

                                <template #cell(completed_rate)="data">
                                    <div class="" style="width: 200px;">
                                        <b-progress :max="100" height="1.4rem">
                                            <b-progress-bar
                                                :value="data.item.status_code === 'completed' ? 100 : Number((data.value * 100).toFixed(1))"
                                                :label="`${data.item.status_code === 'completed' ? Number(100).toFixed(1) : Number(data.value * 100).toFixed(1)} %`"
                                                :style="[{ backgroundColor: data.item.status_code === 'completed' ? `${stats.colors['완료됨']} !important` : `${stats.colors[data.item.status_code]} !important` }]"
                                            >
                                            </b-progress-bar>
                                        </b-progress>
                                    </div>
                                </template>

                                <template #cell(show_details)="row">
                                    <b-btn size="sm" class="mr-2" @click="row.toggleDetails">
                                        {{ $t('상세') }}
                                        <fa v-if="row.detailsShowing" icon="chevron-up"/>
                                        <fa v-else icon="chevron-down"/>
                                    </b-btn>
                                </template>

                                <template #row-details="row">
                                    <div class="d-flex border rounded bg-white mb-3">
                                        <div class="flex-grow-1 p-3">
                                            <p class="font-weight-normal mb-2">{{ $t('담당자 이메일') }}: </p>
                                            <h6 class="d-inline">{{ row.item.requested_e_mail }}</h6>
                                        </div>
                                    </div>
                                    <b-list-group horizontal>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('신청일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.requested_ts">
                                                        <!-- --gray-600 -->
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.requested_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.requested_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.requested_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.requested_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('답변 시작일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.in_progress_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.in_progress_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.in_progress_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.in_progress_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.in_progress_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('답변 종료 예정일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.duedate_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.duedate_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.duedate_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.duedate_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.duedate_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('평가일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.in_assessment_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.in_assessment_ts_dis.date }}</span><br>
                                                            <span>{{ row.item.in_assessment_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.in_assessment_ts_dis_en.date }}</span>
                                                            <br>
                                                            <span>{{ row.item.in_assessment_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('완료일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.completed_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.completed_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.completed_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.completed_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.completed_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                    </b-list-group>
                                </template>
                            </b-table>
                            <b-row no-gutters class="align-items-center justify-content-center mb-3 px-4">
                                <b-col cols="auto">
                                    <b-pagination
                                        v-model="participantTableCurrentPage"
                                        :total-rows="participants.length"
                                        :per-page="selectedParticipantTablePerPage"
                                        :disabled="isParticipantsTableBusy"
                                        aria-controls="total-participant-table"
                                        class="dash-pagination mb-0"
                                    />
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </qesg-dash-table-card>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import dayjs from 'dayjs';
import {mapGetters, mapMutations} from 'vuex';
import {BIconArrowRepeat, BIconFilter, BIconSearch} from 'bootstrap-vue';
import initializeDataProgress from '@shared_mixins/contractor-dashboard/initialize-data-progress';
import transformParticipantsList from '@shared_mixins/contractor-dashboard/transformParticipantsList.js';
import downloadFile from '@shared_mixins/downloadFile';
import QesgDashCard from '@shared_components/contractor/components/qesg-dash-card';
import QesgDashChartCard from '@shared_components/contractor/components/qesg-dash-chart-card';
import QesgDashCardWrapper from '@shared_components/contractor/components/qesg-dash-card-wrapper.vue';
import QesgDashCardIcon from '@shared_components/contractor/components/qesg-dash-card-icon.vue';
import QesgDashTableCard from '@shared_components/contractor/components/qesg-dash-table-card.vue';
import loadingView from '@shared_components/common/loading-view.vue';

export default {
    name: 'contractor-progress-v2',
    props: {
        loadedRequiredData: {
            required: true,
            type: Boolean
        }
    },
    mixins: [
        downloadFile,
        initializeDataProgress,
        transformParticipantsList
    ],
    components: {
        BIconArrowRepeat,
        BIconFilter,
        BIconSearch,
        loadingView,
        QesgDashCard,
        QesgDashChartCard,
        QesgDashCardWrapper,
        QesgDashCardIcon,
        QesgDashTableCard
    },
    data() {
        return {
            isLoadProjectData: false,
            statsChartInstance: null,
            participnatName: null,
            participnatfilterForm: {
                company_name: null,
                status_code: [],
                requested_ts: {start: null, end: null},
                in_progress_ts: {start: null, end: null},
                in_assessment_ts: {start: null, end: null},
                completed_ts: {start: null, end: null},
                duedate_ts: {start: null, end: null},
                campaign_id: null,
            },
            participants: [],
            selectedParticipantTablePerPage: 100,
            participantTableCurrentPage: 1,
            isParticipantSearching: false,
            isParticipantRefreshing: false,
            isParticipantDownloading: false,
            isParticipantAdding: false,
            nextParticipantIndex: 1,
            collapsedParticipantFilterForm: false,
            latest_search_params: {filter: 'all'},
            selectableCampaigns: [],
        };
    },
    computed: {
        ...mapGetters('contractor', ['Get_Contractor', 'GET_ProjectList']),
        date_picker_locale() {
            if (this.$i18n.locale === 'ca') {
                return 'zh-cn';
            }
            return this.$i18n.locale;
        },
        participantsFields() {
            return [
                {
                    key: 'index',
                    label: this.$t('번호'),
                    stickyColumn: true,
                    sortable: false,
                    thClass: 'h6 mb-0',
                    thStyle: {
                        width: '80px'
                    },
                },
                {
                    key: 'company_name',
                    label: this.$t('참여기업명'),
                    stickyColumn: true,
                    sortable: true,
                    thClass: 'h6 mb-0'
                },
                {
                    key: 'campaign_id',
                    label: this.$t('캠페인'),
                    stickyColumn: true,
                    sortable: true,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: '120px'
                    },
                    tdClass: 'text-center',
                },
                {
                    key: 'status_code',
                    label: this.$t('상태'),
                    stickyColumn: true,
                    sortable: true,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: this.$i18n.locale === 'en' ? '150px' : '100px'
                    },
                    tdClass: 'text-center',
                },
                {
                    key: 'completed_rate',
                    label: this.$t('진행률'),
                    stickyColumn: true,
                    sortable: true,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: '224px'
                    },
                    tdClass: 'text-center',
                },
                {
                    key: 'show_details',
                    label: this.$t('상세'),
                    stickyColumn: true,
                    sortable: false,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: '120px'
                    },
                    tdClass: 'text-center',
                }
            ];
        },
        participantTablePerPageOpt() {
            if (this.$i18n.locale === 'ko') {
                return [
                    {text: '50개씩 보기', value: 50},
                    {text: '75개씩 보기', value: 75},
                    {text: '100개씩 보기', value: 100},
                    {text: '150개씩 보기', value: 150},
                    {text: '200개씩 보기', value: 200}
                ];
            } else if (this.$i18n.locale === 'en') {
                return [
                    {text: 'View 50 per page', value: 50},
                    {text: 'View 75 per page', value: 75},
                    {text: 'View 100 per page', value: 100},
                    {text: 'View 150 per page', value: 150},
                    {text: 'View 200 per page', value: 200}
                ];
            } else {
                return [
                    {text: '每页显示50条', value: 50},
                    {text: '每页显示75条', value: 75},
                    {text: '每页显示100条', value: 100},
                    {text: '每页显示150条', value: 150},
                    {text: '每页显示200条', value: 200}
                ];
            }
        },
        apiRequestProjectCode() {
            return this.$route.query.projectid;
        },
        projectEndDate() {
            const _active_project = this.$store.getters['contractor/GET_ProjectList'].filter(_project => _project.project_code === this.apiRequestProjectCode);
            return !_active_project ? null : _active_project[0].target_end_date;
        },
        isParticipantsTableBusy() {
            return this.isParticipantSearching || this.isLoadProjectData || this.isParticipantRefreshing || this.isParticipantDownloading || this.isParticipantAdding;
        },
        participantsTableStatusStatement() {
            if (this.isLoadProjectData) {
                return this.$t('참여기업 리스트를 불러오는 중입니다.');
            } else if (this.isParticipantSearching) {
                return this.$t('참여기업 리스트을 조회 중입니다.');
            } else if (this.isParticipantRefreshing) {
                return this.$t('참여기업 리스트를 초기화 중입니다.');
            } else if (this.isParticipantDownloading) {
                return this.$t('참여기업 리스트 엑셀파일을 다운로드 중입니다.');
            } else {
                return this.$t('참여기업 리스트를 불러오는 중입니다.');
            }
        },
        statsPopoverConfig() {
            return {
                html: true,
                title: () => {
                    return this.$t('진행현황 상태 도움말');
                },
                content: () => {
                    // Note this is called only when the popover is opened
                    return `
                        <div>
                            <div> <span class="mr-2">${this.$t('미신청')}: ${this.$t('ESG 평가 미신청')}</span> </div>
                            <div> <span class="mr-2">${this.$t('신청함')}: ${this.$t('ESG 평가 신청')}</span> </div>
                            <div> <span class="mr-2">${this.$t('답변중')}: ${this.$t('ESG 평가 질문지 답변중')}</span> </div>
                            <div> <span class="mr-2">${this.$t('보완중')}: ${this.$t('ESG 평가 질문지 보완 답변중')}</span> </div>
                            <div> <span class="mr-2">${this.$t('평가중')}: ${this.$t('ESG 답변 평가중')}</span> </div>
                            <div> <span class="mr-2">${this.$t('완료됨')}: ${this.$t('ESG 평가 완료')}</span> </div>
                            <div> <span class="mr-2">${this.$t('취소됨')}: ${this.$t('ESG 평가 취소')}</span> </div>
                            <div> <span class="mr-2">${this.$t('에코바디스')}: "${this.$t('에코바디스 평가사')}" ${this.$t('ESG 평가로 대체')}</span> </div>
                        </div>
                    `;
                }
            };
        },
        currentParticipantsCount() {
            if (!this.$store.getters['contractor/GET_ProjectList'].length) {
                return {total: 0, current: 0};
            } else {
                const _project_code = this.apiRequestProjectCode;
                const _project_index = this.GET_ProjectList.findIndex(_project => _project.project_code === _project_code);
                if (_project_index > -1) {
                    return {
                        total: this.GET_ProjectList[_project_index].project_company_num,
                        current: this.participants.length
                    };
                } else {
                    return {total: 0, current: 0};
                }
            }
        }
    },
    watch: {
        '$route.query.projectid': {
            async handler(_projectid) {
                this.participants = [];
                if (_projectid && ((_projectid === 'latest' && this.$store.getters['contractor/GET_ProjectList'].length) || _projectid !== 'latest')) {
                    const _project_code = _projectid !== 'latest' ? _projectid : this.$store.getters['contractor/GET_ProjectList'][0].project_code;
                    await this.requestProjectProgress(_project_code);
                }
                await this.onResetParticipantFilter();
                this.collapsedParticipantFilterForm = false;
            }
        },
        isLoadProjectData(status) {
            if (status) {
                this.stats.total = 0;
                this.stats.fields = {'신청함': 0, '답변중': 0, '보완중': 0, '평가중': 0, '완료됨': 0, '취소됨': 0, '미신청': 0, '에코바디스': 0};
            }

            this.$nextTick(() => {
                if (!status) {
                    this.drawPieChart();

                }
            });
        },
        loadedRequiredData: {
            async handler(_state, _old_state) {
                if (_state && !_old_state) {
                    await this.requestProjectProgressInitial();
                }
            }
        }
    },
    created() {
        this.participants = [];
        this.$emit('emit-loaded-status', false);
    },
    async mounted() {
        this.$nextTick(() => {
            this.drawPieChart();
        });

        if (this.loadedRequiredData) {
            await this.requestProjectProgressInitial();
        }

        this.$nextTick(() => {
            const totalParticipantTable = document.getElementById('total-participant-table');
            if (totalParticipantTable) {
                totalParticipantTable.parentElement.classList.add('b-table-sticky-header-sm');
            }
        });
    },
    beforeDestroy() {
        const totalParticipantTable = document.getElementById('total-participant-table');
        if (totalParticipantTable) {
            totalParticipantTable.parentElement.classList.remove('b-table-sticky-header-sm');
        }
    },
    methods: {
        ...mapMutations('contractor', ['SET_PROJECT_LIST', 'SET_CONTRACTOR_REQUEST_TIME']),
        // 응답이 없는 오류(네트워크 단절·타임아웃)도 안내한다
        emitLoadError(error) {
            const _status = error?.response?.status === 401 ? 'not-allowed' : 'not-recognized';
            this.$emit('emit-error-status', {status: true, message: _status});
        },
        async requestProjectProgressInitial() {
            if (this.isLoadProjectData) return;
            try {

                this.isLoadProjectData = true;
                this.$emit('emit-loaded-status', false);

                let _res_projects = {};
                const { data :_res_projects_requests} =  await this.$projectsAxios.get(`/${this.$route.query.projectid}/overall`);


                _res_projects.project_stats =  _res_projects_requests?.stats == null
                    ? this.stats.fields
                    : _res_projects_requests.stats;

                _res_projects.project_participant = _res_projects_requests?.participant == null ? [] : _res_projects_requests.participant;
                _res_projects.project_date = _res_projects_requests?.info == null
                    ? {
                        project_code: this.apiRequestProjectCode, target_end_date: null,
                        start_date: null, end_date: null
                    }
                    : _res_projects_requests.info;


                _res_projects.project_stats = Object.keys(this.statsLabel).reduce((_stats, _field_kr) => {
                    const _field_en = this.statsLabel[_field_kr];
                    _stats[_field_kr] = _res_projects.project_stats[_field_en] === null ? 0 : _res_projects.project_stats[_field_en];
                    return _stats;
                }, {});

                const isEmptyPropject = Object.values(_res_projects.project_stats).every(_stat => _stat === 0);
                if (!isEmptyPropject) {
                    this.stats = {
                        total: Object.values(_res_projects.project_stats).reduce((_total, _val) => _total + _val, 0),
                        fields: _res_projects.project_stats,
                        colors: this.stats.colors
                    };
                }

                let _project_dates = _res_projects.project_date;

                this.projectDates = {
                    ..._project_dates,
                    start_date_dis: !_project_dates.start_date || _project_dates.start_date === 'None' ? '미지정' : dayjs(_project_dates.start_date).format('YYYY년 MM월 DD일'),
                    end_date_dis: !_project_dates.end_date || _project_dates.end_date === 'None' ? '미지정' : dayjs(_project_dates.end_date).format('YYYY년 MM월 DD일'),
                    target_end_date_dis: !_project_dates.target_end_date || _project_dates.target_end_date === 'None' ? '미지정' : dayjs(_project_dates.target_end_date).format('YYYY년 MM월 DD일'),
                    start_date_dis_en: !_project_dates.start_date || _project_dates.start_date === 'None' ? '미지정' : dayjs(_project_dates.start_date).format('MM / DD / YYYY'),
                    end_date_dis_en: !_project_dates.end_date || _project_dates.end_date === 'None' ? '미지정' : dayjs(_project_dates.end_date).format('MM / DD / YYYY'),
                    target_end_date_dis_en: !_project_dates.target_end_date || _project_dates.target_end_date === 'None' ? '미지정' : dayjs(_project_dates.target_end_date).format('MM / DD / YYYY')
                };

                this.participants = this.transformParticipantsList(_res_projects.project_participant);

                this.nextParticipantIndex = 1;
                this.SET_CONTRACTOR_REQUEST_TIME();
            } catch (error) {
                this.emitLoadError(error);
            } finally {
                this.isLoadProjectData = false;
                this.$emit('emit-loaded-status', true);
            }

        },
        async requestProjectProgress() {
            if (this.isLoadProjectData) return;
            try {
                this.isLoadProjectData = true;
                this.$emit('emit-loaded-status', false);

                const { data :_res_projects_requests} =  await this.$projectsAxios.get(`/${this.$route.query.projectid}/overall`);

                let _project_dates = _res_projects_requests.info;

                this.stats = {
                    total: Object.values(_res_projects_requests.stats).reduce((_total, _val) => _total + _val, 0),
                    fields: Object.keys(this.statsLabel).reduce((_stats, _field_kr) => {
                        const _field_en = this.statsLabel[_field_kr];
                        _stats[_field_kr] = _res_projects_requests.stats[_field_en] === null ? 0 : _res_projects_requests.stats[_field_en];
                        return _stats;
                    }, {}),
                    colors: this.stats.colors
                };

                this.projectDates = {
                    ..._project_dates,
                    start_date_dis: !_project_dates.start_date ? '미지정' : dayjs(_project_dates.start_date).format('YYYY년 MM월 DD일'),
                    end_date_dis: !_project_dates.end_date ? '미지정' : dayjs(_project_dates.end_date).format('YYYY년 MM월 DD일'),
                    target_end_date_dis: !_project_dates.target_end_date ? '미지정' : dayjs(_project_dates.target_end_date).format('YYYY년 MM월 DD일'),
                    start_date_dis_en: !_project_dates.start_date ? '미지정' : dayjs(_project_dates.start_date).format('MM / DD / YYYY'),
                    end_date_dis_en: !_project_dates.end_date ? '미지정' : dayjs(_project_dates.end_date).format('MM / DD / YYYY'),
                    target_end_date_dis_en: !_project_dates.target_end_date ? '미지정' : dayjs(_project_dates.target_end_date).format('MM / DD / YYYY')
                };

                this.participants = this.transformParticipantsList(_res_projects_requests.participant);

                this.SET_CONTRACTOR_REQUEST_TIME();
            } catch (error) {
                this.emitLoadError(error);
            } finally {
                this.isLoadProjectData = false;
                this.$emit('emit-loaded-status', true);
            }
        },
        async requestMoreParticipants() {
            this.isParticipantAdding = true;
            this.nextParticipantIndex += 100;
            // let _res_project_participants = await this.$projectsAxios.get(process.env.VUE_APP_BACKENDHOST + `/projects/participant/${this.apiRequestProjectCode}?filter=all&index=${this.nextParticipantIndex}`)
            let _res_project_participants = await this.$projectsAxios.get(`/participant/${this.apiRequestProjectCode}?filter=all&index=${this.nextParticipantIndex}`);
            this.participants.push(...this.transformParticipantsList(_res_project_participants.data));
            this.isParticipantAdding = false;
            this.SET_CONTRACTOR_REQUEST_TIME();
        },
        drawPieChart() {
            const data = {
                labels: Object.keys(this.stats.fields),
                datasets: [{
                    label: '진행현황 요약',
                    data: this.stats.total !== 0 ? Object.values(this.stats.fields) : Object.values(this.stats.fields).map(() => 10),
                    backgroundColor: this.stats.total !== 0 ? Object.values(this.stats.colors) : '#CFD8DC',
                    hoverOffset: 4,
                    borderWidth: 3
                }]
            };

            const _stat_chart_options = {
                maintainAspectRatio: false,
                cutout: 20,
                plugins: {
                    datalabels: {
                        font: {
                            size: 15
                        },
                        formatter: (value) => {
                            if (!this.stats.total) {
                                return '0 %';
                            } else {
                                return value === 0
                                    ? '0%'
                                    : Math.round((value / this.stats.total) * 100) + ' %';
                            }
                        },
                        display: (context) => {
                            const _value = context.dataset.data[context.dataIndex];
                            if (this.stats.total === 0) {
                                return true;
                            } else {
                                if (_value === 0) {
                                    return false;
                                } else {
                                    const _share = Math.round((_value / this.stats.total) * 100);
                                    return _share > 10;
                                }
                            }
                        }
                    },
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                let label = context.label || '';
                                let _raw_value = this.stats.total === 0 ? 0 : context.parsed;
                                return ` ${label}: ${_raw_value}개 기업`;
                            }
                        }
                    }
                }
            };

            if (!this.statsChartInstance) {
                this.statsChartInstance = this.$chartjs.createChart(
                    document.getElementById('progress-pie-chart'),
                    {type: 'doughnut', data, options: _stat_chart_options}
                );
            } else {
                this.$chartjs.updateChart(this.statsChartInstance, data, _stat_chart_options);
            }
        },
        async viewParticipantByLegend(label_kr) {
            this.onResetParticipantFilter();
            const label_en = this.statsLabel[label_kr];
            const isExistedStatus = Object.values(this.statsLabel).includes(label_en);
            if (isExistedStatus) {
                this.participnatfilterForm.status_code.push(label_en);
            }
            await this.onSubmitParticipantFilter();

        },
        async searchParticipantByName() {
            try {
                this.isParticipantSearching = true;
                var params = {condition: {company_name: this.participnatfilterForm.company_name}, filter: 'search'};
                const searched_by_participant_name = await this.$projectsAxios({
                    method: 'get',
                    url: `participant/${this.apiRequestProjectCode}`,
                    params: params
                });
                // 최근 검색 결과를 가지고 Excel을 다운 받을 수 있도록 하기 위함.
                this.latest_search_params = params;
                this.participants = this.transformParticipantsList(searched_by_participant_name.data.list);
            } catch (error) {
                console.log(' ~ error => ', error);
                if (error.response) {
                    console.log(' ~ error.response => ', error.response);
                }
                this.$EventBus.$emit('toast-failure', this.$t('참여기업 조회'), this.$t('참여기업 리스트를 조회하지 못했습니다. 표시된 목록은 이전 조회 결과입니다. 다시 시도해주세요.'));
            } finally {
                this.isParticipantSearching = false;
            }
        },
        async refreshParticipants() {
            this.isParticipantRefreshing = true;

            const _participant_filtered_res = await this.$projectsAxios({
                method: 'get',
                url: `/participant/${this.apiRequestProjectCode}`,
                params: {filter: 'all'}
            });

            this.participants = this.transformParticipantsList(_participant_filtered_res.data.list);
            this.isParticipantRefreshing = false;
            this.SET_CONTRACTOR_REQUEST_TIME();
        },
        validateParticipantFilter() {
            const _condition = {};
            for (const [key, value] of Object.entries(this.participnatfilterForm)) {
                let _value = null;
                if (key.includes('_ts')) {
                    if (value.start && value.end) {
                        _value = value;
                    } else if (value.start && !value.end) {
                        _value = value;
                        _value.start = new Date(_value.start);
                        _value.start.setMonth(_value.start.getMonth() + 1);
                        if (this.projectEndDate) {
                            let _end_date = new Date(this.projectEndDate);
                            _end_date.setMonth(_end_date.getMonth() + 1);
                            _value.end = `${_end_date.getFullYear()}-${_end_date.getMonth()}-${_end_date.getDate()}`;
                        } else {
                            let _end_date = new Date(_value.start);
                            _end_date.setMonth(_end_date.getMonth() + 1);
                            _value.end = `${_end_date.getFullYear()}-${_end_date.getMonth()}-${_end_date.getDate()}`;
                        }
                        _value.start = `${_value.start.getFullYear()}-${_value.start.getMonth()}-${_value.start.getDate()}`;
                    } else if (!value.start && value.end) {
                        _value = value;
                        let _start_date = new Date(value.end);
                        _start_date.setMonth(_start_date.getMonth() - 2);
                        _value.start = `${_start_date.getFullYear()}-${_start_date.getMonth()}-${_start_date.getDate()}`;
                    }
                } else {
                    if (Array.isArray(value) && value.length) {
                        _value = value;
                    }

                    if (typeof value === 'string' && value) {
                        _value = value;
                    }
                }
                if (_value) {
                    _condition[key] = _value;
                }
            }
            return {
                valid: !!Object.keys(_condition).length,
                value: _condition
            };
        },
        async onSubmitParticipantFilter() {
            try {
                this.isParticipantSearching = true;
                const isValidFilterForm = this.validateParticipantFilter();
                let _participant_filtered_request_option = null;
                var params = null;
                if (!isValidFilterForm.valid) {
                    params = {
                        filter: 'all'
                    };
                } else {
                    params = {
                        filter: 'search',
                        condition: isValidFilterForm.value
                    };
                }
                _participant_filtered_request_option = {
                    url: `/participant/${this.apiRequestProjectCode}`,
                    params: params
                };
                const _participant_filtered_res = await this.$projectsAxios({
                    method: 'get',
                    ..._participant_filtered_request_option
                });
                this.latest_search_params = params;

                this.participants = this.transformParticipantsList(_participant_filtered_res.data.list);
            } catch (error) {
                console.log(' ~ error => ', error);
                if (error.response) {
                    console.log(' ~ error.response => ', error.response);
                }
                this.$EventBus.$emit('toast-failure', this.$t('참여기업 조회'), this.$t('참여기업 리스트를 조회하지 못했습니다. 표시된 목록은 이전 조회 결과입니다. 다시 시도해주세요.'));
            } finally {
                this.isParticipantSearching = false;
                this.SET_CONTRACTOR_REQUEST_TIME();
            }
        },
        async downloadParticipantsExcel() {
            try {
                this.isParticipantDownloading = true;

                await this.onSubmitParticipantFilter();

                //회사이름 기준 검색 및 필터링 검색 완료 시, 저장된 params (최근 검색 조건)으로 Excel을 Download
                //UI에서 보이는 것과 출력되는 Excel의 데이터를 일치시키기 위함.
                let _participant_filtered_download_option = {
                    url: `/excel/${this.apiRequestProjectCode}`,
                    params: this.latest_search_params
                };

                const _res_participants_excel = await this.$projectsAxios({
                    method: 'get',
                    ..._participant_filtered_download_option,
                    responseType: 'blob'
                });

                let fileName = '';
                switch (this.$i18n.locale) {
                case 'ko':
                    fileName = `${this.apiRequestProjectCode}_참여기업_진행현황_데이터.xlsx`;
                    break;
                case 'en':
                    fileName = `${this.apiRequestProjectCode}_participants_progress_data.xlsx`;
                    break;
                case 'ca':
                    fileName = `${this.apiRequestProjectCode}_参与企业_进展状态_数据.xlsx`;
                    break;
                default:
                    fileName = `${this.apiRequestProjectCode}_참여기업_진행현황_데이터.xlsx`;
                    break;
                }

                this.downloadFileFromResponse(_res_participants_excel, fileName);

            } catch (error) {
                console.log(' ~ error => ', error);
                if (error.response) {
                    console.log(' ~ error.response => ', error.response);
                }
                this.$EventBus.$emit('toast-failure', this.$t('참여기업 엑셀 다운로드'), this.$t('엑셀 다운로드 중 오류가 발생했습니다. 다시 시도해주세요.'));
            } finally {
                this.isParticipantDownloading = false;
                this.SET_CONTRACTOR_REQUEST_TIME();
            }

        },
        async onResetParticipantFilter(needRefresh = false) {
            this.participnatfilterForm = {
                company_name: null,
                status_code: [],
                requested_ts: {start: null, end: null},
                in_progress_ts: {start: null, end: null},
                in_assessment_ts: {start: null, end: null},
                completed_ts: {start: null, end: null},
                duedate_ts: {start: null, end: null}
            };

            if (needRefresh) {
                await this.onSubmitParticipantFilter();
            }
        }
    }
};
</script>

<style>

</style>
